import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Button from './ui/Button';

const aboutLinks = [
  { name: 'Our Team', to: '/team' },
  { name: 'Who We Serve', to: '/who-we-serve' },
  { name: 'Our Community', to: '/community' }
];

const howWeWorkLinks = [
  { name: 'Services', to: '/services' },
  { name: 'Process', to: '/process' },
  { name: 'Tax Preparation', to: '/how-we-work/tax-preparation' }
];

const desktopLinks = [
  { name: 'Insights', to: '/insights' },
  { name: 'Contact', to: '/contact' }
];

function useHoverMenu() {
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeRef.current) {
        clearTimeout(closeRef.current);
      }
    };
  }, []);

  const cancelClose = () => {
    if (closeRef.current) {
      clearTimeout(closeRef.current);
      closeRef.current = null;
    }
  };

  const onEnter = () => {
    cancelClose();
    setOpen(true);
  };

  const onLeave = () => {
    cancelClose();
    closeRef.current = setTimeout(() => {
      setOpen(false);
      closeRef.current = null;
    }, 120);
  };

  return { open, setOpen, onEnter, onLeave, cancelClose };
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const burgerButtonRef = useRef(null);
  const drawerContentRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const isTransparentAtTop = isHomePage && !isScrolled && !open;

  useEffect(() => {
    if (!open) {
      if (previouslyFocusedRef.current instanceof HTMLElement) {
        previouslyFocusedRef.current.focus();
      } else if (burgerButtonRef.current) {
        burgerButtonRef.current.focus();
      }
      return undefined;
    }

    previouslyFocusedRef.current = document.activeElement;

    const focusableSelectors =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = drawerContentRef.current
      ? Array.from(
          drawerContentRef.current.querySelectorAll(focusableSelectors)
        )
      : [];

    if (focusables.length) {
      focusables[0].focus();
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key === 'Tab' && focusables.length) {
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    let ticking = false;

    const updateScrolledState = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 24);
        ticking = false;
      });
    };

    updateScrolledState();
    window.addEventListener('scroll', updateScrolledState, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateScrolledState);
    };
  }, [location.pathname]);

  useEffect(() => {
    const topBar = document.getElementById('top-contact-bar');
    if (!topBar) return undefined;

    const updateTopBarHeight = () => {
      setTopBarHeight(topBar.offsetHeight ?? 0);
    };

    updateTopBarHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateTopBarHeight();
      });
      observer.observe(topBar);

      return () => {
        observer.disconnect();
      };
    }

    window.addEventListener('resize', updateTopBarHeight);

    return () => {
      window.removeEventListener('resize', updateTopBarHeight);
    };
  }, []);

  function handleNavClick(event, to) {
    if (!to.includes('#')) {
      setOpen(false);
      return;
    }

    event.preventDefault();
    setOpen(false);
    const [, hash] = to.split('#');
    if (!hash) return;

    if (window.location.pathname !== '/') {
      window.location.href = `/${hash ? `#${hash}` : ''}`;
      return;
    }

    const target = document.getElementById(hash);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      window.history.replaceState(null, '', `#${hash}`);
    }
  }

  return (
    <header
      style={isTransparentAtTop ? { top: `${topBarHeight}px` } : undefined}
      className={`z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-out ${
        isHomePage
          ? isTransparentAtTop
            ? 'fixed inset-x-0 border-b border-transparent bg-transparent shadow-none backdrop-blur-0'
            : 'fixed inset-x-0 top-0 border-b border-cam-blue/10 bg-white/90 shadow-sm shadow-slate-900/5 backdrop-blur-none md:backdrop-blur'
          : 'border-b border-cam-blue/10 bg-white/90 shadow-sm shadow-slate-900/5 backdrop-blur-none md:backdrop-blur'
      } ${isHomePage ? '' : 'sticky top-0'}`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between gap-6 py-4">
          <div className="flex flex-1 items-center gap-3">
            <NavLink
              to="/"
              className="flex items-center transition"
              aria-label="Carter Asset Management home"
            >
              <img
                src="/images/Carter AM Site Logo.png"
                alt="Carter Asset Management"
                className={`site-logo block h-11 w-auto object-contain transition md:h-12 lg:h-[3.125rem] ${
                  isTransparentAtTop ? 'drop-shadow-[0_2px_14px_rgba(15,23,42,0.32)]' : ''
                }`}
              />
            </NavLink>
          </div>
          <nav
            className={`hidden flex-1 items-center justify-center gap-6 text-sm font-medium lg:flex ${
              isTransparentAtTop ? 'text-white' : 'text-slate-600'
            }`}
            aria-label="Primary navigation"
          >
            <AboutMenu onNavigate={handleNavClick} isTransparent={isTransparentAtTop} />
            <HowWeWorkMenu onNavigate={handleNavClick} isTransparent={isTransparentAtTop} />
            {desktopLinks.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `transition ${
                    isTransparentAtTop ? 'hover:text-white/80' : 'hover:text-cam-blue'
                  } ${
                    isActive
                      ? 'text-cam-blue underline decoration-2 underline-offset-4'
                      : isTransparentAtTop
                        ? 'text-white'
                        : 'text-slate-600'
                  }`
                }
                onClick={(event) => handleNavClick(event, item.to)}
              >
                {item.name}
              </NavLink>
            ))}
          </nav>
          <div className="flex flex-1 items-center justify-end gap-4">
            <div className="hidden lg:block">
              <Button
                variant={isTransparentAtTop ? 'ghost' : 'primary'}
                className={`focus:ring-offset-white ${
                  isTransparentAtTop
                    ? 'border border-white/70 bg-transparent text-white shadow-[0_8px_22px_rgba(15,23,42,0.24)] hover:bg-white/10 hover:text-white'
                    : ''
                }`}
                href="https://login.orionadvisor.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Login
              </Button>
            </div>
            <button
              type="button"
              className={`inline-flex items-center justify-center rounded-full border p-2 transition lg:hidden ${
                isTransparentAtTop
                  ? 'border-white/40 text-white hover:border-white/60 hover:bg-white/10'
                  : 'border-cam-blue/30 text-cam-blue hover:border-cam-blue hover:bg-cam-blue/5'
              }`}
              ref={burgerButtonRef}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5m-16.5 5.25h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div
        className={`${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        } fixed inset-0 z-50 flex bg-slate-900/40 backdrop-blur-sm transition-opacity lg:hidden`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
      >
        <div className="w-full" onClick={() => setOpen(false)} aria-hidden="true" />
        <div
          ref={drawerContentRef}
          className={`${open ? 'translate-x-0' : 'translate-x-full'} relative ml-auto flex h-full w-80 max-w-[80vw] flex-col bg-white shadow-2xl transition-transform`}
        >
          <div className="flex items-center justify-between border-b border-cam-blue/10 px-6 py-5">
            <span
              id="mobile-menu-title"
              className="text-sm font-semibold uppercase tracking-wide text-cam-blue"
            >
              Menu
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-cam-blue/20 p-2 text-cam-blue transition hover:bg-cam-blue/5"
              aria-label="Close menu"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav
            className="mobile-nav flex-1 overflow-y-auto"
            aria-label="Mobile site navigation"
          >
            <section className="mobile-nav__section">
              <h3 className="mobile-nav__heading">About</h3>
              <ul className="mobile-nav__list">
                {aboutLinks.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `mobile-nav__link ${isActive ? 'is-active' : ''}`
                      }
                      onClick={(event) => handleNavClick(event, item.to)}
                    >
                      {item.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mobile-nav__section">
              <h3 className="mobile-nav__heading">How We Work</h3>
              <ul className="mobile-nav__list">
                {howWeWorkLinks.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `mobile-nav__link ${isActive ? 'is-active' : ''}`
                      }
                      onClick={(event) => handleNavClick(event, item.to)}
                    >
                      {item.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mobile-nav__section">
              <h3 className="mobile-nav__heading">More</h3>
              <ul className="mobile-nav__list">
                {desktopLinks.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `mobile-nav__link ${isActive ? 'is-active' : ''}`
                      }
                      onClick={(event) => handleNavClick(event, item.to)}
                    >
                      {item.name === 'Contact' ? 'Contact / Schedule' : item.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          </nav>
          <div className="border-t border-cam-blue/10 p-6">
            <Button variant="primary" className="w-full" href="/login">
              Login
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

function AboutMenu({ onNavigate, isTransparent = false }) {
  const { open, setOpen, onEnter, onLeave, cancelClose } = useHoverMenu();
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (open && document.activeElement === buttonRef.current && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    return undefined;
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onLeave();
        }
      }}
      onFocusCapture={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          cancelClose();
          setOpen(false);
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`inline-flex items-center gap-1 px-2 py-1 font-medium whitespace-nowrap transition focus:outline-none ${
          isTransparent ? 'text-white hover:text-white/80' : 'text-slate-800 hover:text-cam-blue'
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          cancelClose();
          setOpen((value) => !value);
        }}
      >
        About
        <svg className="h-4 w-4" aria-hidden="true" viewBox="0 0 16 16">
          <path d="M6 8l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      <div
        role="menu"
        className={`absolute left-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 transition ${
          open ? 'pointer-events-auto opacity-100 translate-y-0' : 'pointer-events-none opacity-0 -translate-y-1'
        }`}
      >
        {aboutLinks.map((link, index) => (
          <NavLink
            key={link.name}
            to={link.to}
            role="menuitem"
            className={({ isActive }) =>
              `block px-4 py-2 text-sm transition ${
                isActive ? 'bg-cam-blue/10 text-cam-blue' : 'text-slate-700 hover:bg-slate-50'
              }`
            }
            ref={index === 0 ? firstItemRef : undefined}
            onClick={(event) => {
              onNavigate?.(event, link.to);
              cancelClose();
              setOpen(false);
            }}
          >
            {link.name}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function HowWeWorkMenu({ onNavigate, isTransparent = false }) {
  const { open, setOpen, onEnter, onLeave, cancelClose } = useHoverMenu();
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const firstItemRef = useRef(null);

  useEffect(() => {
    if (open && document.activeElement === buttonRef.current && firstItemRef.current) {
      firstItemRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    return undefined;
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={onEnter}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onLeave();
        }
      }}
      onFocusCapture={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          cancelClose();
          setOpen(false);
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`inline-flex items-center gap-1 px-2 py-1 font-medium whitespace-nowrap transition focus:outline-none ${
          isTransparent ? 'text-white hover:text-white/80' : 'text-slate-800 hover:text-cam-blue'
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          cancelClose();
          setOpen((value) => !value);
        }}
      >
        How We Work
        <svg className="h-4 w-4" aria-hidden="true" viewBox="0 0 16 16">
          <path d="M6 8l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
      <div
        role="menu"
        className={`absolute left-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5 transition ${
          open ? 'pointer-events-auto opacity-100 translate-y-0' : 'pointer-events-none opacity-0 -translate-y-1'
        }`}
      >
        {howWeWorkLinks.map((link, index) => (
          <NavLink
            key={link.name}
            to={link.to}
            role="menuitem"
            className={({ isActive }) =>
              `block px-4 py-2 text-sm transition ${
                isActive ? 'bg-cam-blue/10 text-cam-blue' : 'text-slate-700 hover:bg-slate-50'
              }`
            }
            ref={index === 0 ? firstItemRef : undefined}
            onClick={(event) => {
              onNavigate?.(event, link.to);
              cancelClose();
              setOpen(false);
            }}
          >
            {link.name}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
